/**
 * MH-40 Hồ sơ cá nhân + thiết lập xác thực hai lớp (đợt 3 phần C4, trả nợ D1
 * đợt 0 phần bảo mật đăng nhập), dựng lại theo trang "Thông tin cá nhân" của bản
 * mẫu CMS: lưới 260px | phần còn lại; card trái ảnh đại diện + liên hệ, card phải
 * ba tab. Danh sách phiên đăng nhập (MH-41) nằm ở tệp riêng
 * `MH41ThietBiTinCayPhien.tsx`, liên kết từ đây.
 *
 * Dữ liệu lấy từ hồ sơ phiên (GET /api/v1/auth/me, có e-mail, điện thoại, địa chỉ).
 * Nút "Chỉnh sửa" mở biểu mẫu sửa tại chỗ, lưu bằng PATCH /api/v1/auth/me rồi cập
 * nhật hồ sơ phiên — tên đầu trang đổi theo ngay (hợp đồng A0 hồ sơ – thông báo
 * mục 5). Tab Câu hỏi bảo mật đã bỏ theo quyết định của tỉnh. Tab Đổi mật khẩu gọi
 * thật POST /api/v1/auth/doi-mat-khau (hợp đồng A0 tài khoản xã mục 3).
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { vi } from '../ngon-ngu/vi';
import { viQuanTri } from '../ngon-ngu/vi-quan-tri';
import { quanTriApi, LoiApi } from '../api/quan-tri-api';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import { DoManhMatKhau, matKhauDuManh } from '../thanh-phan/DoManhMatKhau';
import './quan-tri.css';

const t = viQuanTri.caNhan;

type TabHoSo = 'thong-tin' | 'google-auth' | 'doi-mat-khau';

const CAC_TAB: readonly (readonly [TabHoSo, string])[] = [
  ['thong-tin', t.tab.thongTin],
  ['google-auth', t.tab.googleAuth],
  ['doi-mat-khau', t.tab.doiMatKhau],
];

/** E-mail: kiểm hình thức đơn giản; máy chủ kiểm lại. */
export function emailHopLe(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/** Điện thoại: chỉ chữ số, `+`, khoảng trắng; 8–15 chữ số (khớp máy chủ). */
export function dienThoaiHopLe(v: string): boolean {
  if (!/^[0-9+ ]+$/.test(v)) return false;
  const so = v.replace(/[^0-9]/g, '').length;
  return so >= 8 && so <= 15;
}

export function MH40HoSoCaNhan(): ReactNode {
  const phien = dungPhien();
  const hoSo = phien.hoSo;
  const [tab, datTab] = useState<TabHoSo>('thong-tin');
  const [buocThietLap, datBuocThietLap] = useState<'an' | 'quet-ma' | 'ma-du-phong'>('an');
  const [bimatBase32, datBiMatBase32] = useState('');
  const [otpauthUrl, datOtpauthUrl] = useState('');
  const [ma, datMa] = useState('');
  const [maDuPhong, datMaDuPhong] = useState<string[]>([]);
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);
  const [thongBao, datThongBao] = useState<string | null>(null);
  const [mkHienTai, datMkHienTai] = useState('');
  const [mkMoi, datMkMoi] = useState('');
  const [mkNhapLai, datMkNhapLai] = useState('');
  const [dangDoiMk, datDangDoiMk] = useState(false);
  // Sửa hồ sơ tại chỗ.
  const [dangSua, datDangSua] = useState(false);
  const [suaHoTen, datSuaHoTen] = useState('');
  const [suaEmail, datSuaEmail] = useState('');
  const [suaDienThoai, datSuaDienThoai] = useState('');
  const [suaDiaChi, datSuaDiaChi] = useState('');
  const [dangLuuHoSo, datDangLuuHoSo] = useState(false);

  const moSua = (): void => {
    datSuaHoTen(hoSo?.hoTen ?? '');
    datSuaEmail(hoSo?.email ?? '');
    datSuaDienThoai(hoSo?.dienThoai ?? '');
    datSuaDiaChi(hoSo?.diaChi ?? '');
    datLoi(null);
    datThongBao(null);
    datDangSua(true);
  };

  const luuHoSo = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    datLoi(null);
    datThongBao(null);
    const hoTen = suaHoTen.trim();
    const email = suaEmail.trim();
    const dienThoai = suaDienThoai.trim();
    const diaChi = suaDiaChi.trim();
    if (!hoTen) {
      datLoi(t.thieuHoTen);
      return;
    }
    if (email && !emailHopLe(email)) {
      datLoi(t.emailKhongHopLe);
      return;
    }
    if (dienThoai && !dienThoaiHopLe(dienThoai)) {
      datLoi(t.dienThoaiKhongHopLe);
      return;
    }
    datDangLuuHoSo(true);
    try {
      // Chuỗi rỗng = xoá giá trị (hợp đồng A0 hồ sơ – thông báo mục 4).
      const moi = await quanTriApi.suaHoSo({ hoTen, email, dienThoai, diaChi });
      if (hoSo) phien.capNhatHoSo({ ...hoSo, ...(moi ?? { hoTen, email, dienThoai, diaChi }) });
      datDangSua(false);
      datThongBao(t.daLuuHoSo);
    } catch (err) {
      datLoi(err instanceof LoiApi ? `${t.loiLuuHoSo}: ${err.message}` : t.loiLuuHoSo);
    } finally {
      datDangLuuHoSo(false);
    }
  };

  const batDauThietLap = async (): Promise<void> => {
    datLoi(null);
    try {
      const kq = await quanTriApi.thietLap2fa();
      datBiMatBase32(kq.secretBase32);
      datOtpauthUrl(kq.otpauthUrl);
      datBuocThietLap('quet-ma');
    } catch (err) {
      datLoi(err instanceof LoiApi ? err.message : 'Không thiết lập được xác thực hai lớp');
    }
  };

  const kichHoat = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    datDangGui(true);
    datLoi(null);
    try {
      const kq = await quanTriApi.kichHoat2fa(ma);
      datMaDuPhong(kq.maDuPhong);
      datBuocThietLap('ma-du-phong');
      datThongBao(vi.hoSoCaNhan.daBat2fa);
    } catch (err) {
      datLoi(err instanceof LoiApi ? err.message : 'Mã xác thực không đúng');
    } finally {
      datDangGui(false);
    }
  };

  // Địa bàn: tên theo `tenPhamVi`; tài khoản tỉnh phạm vi rỗng → "Toàn tỉnh".
  const tenDiaBan = (hoSo?.tenPhamVi?.length ? hoSo.tenPhamVi : hoSo?.phamViDiaBan)?.join(', ') || t.toanTinh;

  const doiMatKhau = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    datLoi(null);
    datThongBao(null);
    if (!mkHienTai || !matKhauDuManh(mkMoi)) {
      datLoi(viQuanTri.matKhau.chuaDuManh);
      return;
    }
    if (mkMoi !== mkNhapLai) {
      datLoi(viQuanTri.matKhau.khongKhop);
      return;
    }
    if (mkMoi === mkHienTai) {
      datLoi(viQuanTri.matKhau.trungCu);
      return;
    }
    datDangDoiMk(true);
    try {
      await quanTriApi.doiMatKhau({ matKhauHienTai: mkHienTai, matKhauMoi: mkMoi });
      datThongBao(t.daDoiMatKhau);
      datMkHienTai('');
      datMkMoi('');
      datMkNhapLai('');
    } catch (err) {
      // Thông điệp máy chủ đã nói rõ lý do (sai mật khẩu hiện tại, chưa đủ mạnh, trùng cũ).
      datLoi(err instanceof LoiApi ? `${t.loiDoiMatKhau}: ${err.message}` : t.loiDoiMatKhau);
    } finally {
      datDangDoiMk(false);
    }
  };

  const tat = async (): Promise<void> => {
    if (!window.confirm(vi.hoSoCaNhan.tat2fa + '?')) return;
    try {
      await quanTriApi.tat2fa();
      datThongBao(vi.hoSoCaNhan.daTat2fa);
      datBuocThietLap('an');
    } catch (err) {
      datLoi(err instanceof LoiApi ? err.message : 'Thao tác thất bại');
    }
  };

  const noiDungTab: Record<TabHoSo, ReactNode> = {
    'thong-tin': dangSua ? (
      <form className="bieu-mau-hep" data-vung="sua-ho-so" onSubmit={(e) => void luuHoSo(e)} noValidate>
        <div className="truong">
          <label htmlFor="hs-ho-ten">
            {t.ten}
            <span className="truong__bat-buoc">*</span>
          </label>
          <input
            id="hs-ho-ten"
            value={suaHoTen}
            maxLength={100}
            autoComplete="name"
            onChange={(e) => datSuaHoTen(e.target.value)}
          />
        </div>
        <div className="truong">
          <label htmlFor="hs-email">{t.email}</label>
          <input
            id="hs-email"
            type="email"
            value={suaEmail}
            autoComplete="email"
            onChange={(e) => datSuaEmail(e.target.value)}
          />
        </div>
        <div className="truong">
          <label htmlFor="hs-dien-thoai">{t.dienThoai}</label>
          <input
            id="hs-dien-thoai"
            type="tel"
            value={suaDienThoai}
            autoComplete="tel"
            aria-describedby="hs-goi-y-dien-thoai"
            onChange={(e) => datSuaDienThoai(e.target.value)}
          />
          <small id="hs-goi-y-dien-thoai" className="truong__mo-ta">
            {t.goiYDienThoai}
          </small>
        </div>
        <div className="truong">
          <label htmlFor="hs-dia-chi">{t.diaChi}</label>
          <input
            id="hs-dia-chi"
            value={suaDiaChi}
            maxLength={300}
            autoComplete="street-address"
            onChange={(e) => datSuaDiaChi(e.target.value)}
          />
        </div>
        <p className="truong__mo-ta">{t.goiYXoa}</p>
        <div className="man-hinh__hanh-dong">
          <button type="submit" className="nut nut--chinh" disabled={dangLuuHoSo}>
            {dangLuuHoSo ? t.dangLuu : t.luu}
          </button>
          <button type="button" className="nut" disabled={dangLuuHoSo} onClick={() => datDangSua(false)}>
            {t.huy}
          </button>
        </div>
      </form>
    ) : (
      <>
        <dl className="mo-ta-luoi" data-vung="thong-tin-ho-so">
          <div>
            <dt>{t.ten}</dt>
            <dd data-vung="ho-ten">{hoSo?.hoTen ?? '—'}</dd>
          </div>
          <div>
            <dt>{t.dienThoai}</dt>
            <dd data-vung="dien-thoai">{hoSo?.dienThoai || '—'}</dd>
          </div>
          <div>
            <dt>{t.email}</dt>
            <dd data-vung="email">{hoSo?.email || '—'}</dd>
          </div>
          <div>
            <dt>{t.diaChi}</dt>
            <dd data-vung="dia-chi">{hoSo?.diaChi || '—'}</dd>
          </div>
          <div>
            <dt>{vi.hoSoCaNhan.tinh}</dt>
            <dd>{hoSo?.tenTinh ?? '—'}</dd>
          </div>
          <div>
            <dt>{t.cap}</dt>
            <dd data-vung="cap-tai-khoan">{t.capNhan[hoSo?.cap === 'xa' ? 'xa' : 'tinh']}</dd>
          </div>
          <div>
            <dt>{t.diaBan}</dt>
            <dd data-vung="dia-ban-tai-khoan">{tenDiaBan}</dd>
          </div>
          <div>
            <dt>{vi.hoSoCaNhan.nhom}</dt>
            <dd>{hoSo?.nhom?.join(', ') || '—'}</dd>
          </div>
        </dl>
        <button type="button" className="nut" data-vung="nut-sua-ho-so" disabled={!hoSo} onClick={moSua}>
          <BieuTuong ten="nguoi-dung" co={14} /> {t.chinhSua}
        </button>
      </>
    ),
    'google-auth': (
      <section aria-label={vi.hoSoCaNhan.baoMat}>
        <p>{t.moTa2fa}</p>
        {buocThietLap === 'an' ? (
          <div className="man-hinh__hanh-dong">
            <button type="button" className="nut nut--chinh" onClick={() => void batDauThietLap()}>
              {vi.hoSoCaNhan.thietLap2fa}
            </button>
            <button type="button" className="nut" onClick={() => void tat()}>
              {vi.hoSoCaNhan.tat2fa}
            </button>
          </div>
        ) : null}

        {buocThietLap === 'quet-ma' ? (
          <form className="hop-thoai" onSubmit={(e) => void kichHoat(e)}>
            <p>{vi.hoSoCaNhan.quetMa}</p>
            <p>
              <code data-vung="bi-mat-2fa">{bimatBase32}</code>
            </p>
            <p className="the the--phu">{otpauthUrl}</p>
            <label>
              {vi.hoSoCaNhan.nhapMaXacNhan}
              <input
                value={ma}
                onChange={(e) => datMa(e.target.value)}
                inputMode="numeric"
                pattern="[0-9]{6}"
                required
              />
            </label>
            <button type="submit" className="nut nut--chinh" disabled={dangGui}>
              {vi.hoSoCaNhan.kichHoat}
            </button>
            <button type="button" className="nut" onClick={() => datBuocThietLap('an')}>
              {vi.hoSoCaNhan.huy}
            </button>
          </form>
        ) : null}

        {buocThietLap === 'ma-du-phong' ? (
          <div data-vung="ma-du-phong">
            <p>{vi.hoSoCaNhan.maDuPhongTieuDe}</p>
            <ul role="list">
              {maDuPhong.map((m) => (
                <li key={m}>
                  <code>{m}</code>
                </li>
              ))}
            </ul>
            <button type="button" className="nut" onClick={() => datBuocThietLap('an')}>
              {vi.nhatKyThaoTac.dong}
            </button>
          </div>
        ) : null}
      </section>
    ),
    'doi-mat-khau': (
      <form className="bieu-mau-hep" data-vung="doi-mat-khau" onSubmit={(e) => void doiMatKhau(e)} noValidate>
        <div className="truong">
          <label htmlFor="pw0">
            {t.matKhauHienTai}
            <span className="truong__bat-buoc">*</span>
          </label>
          <input
            type="password"
            id="pw0"
            autoComplete="current-password"
            value={mkHienTai}
            onChange={(e) => datMkHienTai(e.target.value)}
          />
        </div>
        <div className="truong">
          <label htmlFor="pw1">
            {t.matKhauMoi}
            <span className="truong__bat-buoc">*</span>
          </label>
          <input
            type="password"
            id="pw1"
            autoComplete="new-password"
            aria-describedby="pw1-goi-y pw1-do-manh"
            value={mkMoi}
            onChange={(e) => datMkMoi(e.target.value)}
          />
          <small id="pw1-goi-y" className="truong__mo-ta">
            {t.goiYMatKhau}
          </small>
          <DoManhMatKhau matKhau={mkMoi} id="pw1-do-manh" />
        </div>
        <div className="truong">
          <label htmlFor="pw2">
            {t.nhapLaiMatKhau}
            <span className="truong__bat-buoc">*</span>
          </label>
          <input
            type="password"
            id="pw2"
            autoComplete="new-password"
            value={mkNhapLai}
            onChange={(e) => datMkNhapLai(e.target.value)}
          />
        </div>
        <button type="submit" className="nut nut--chinh" disabled={dangDoiMk}>
          {dangDoiMk ? t.dangGui : t.capNhatMatKhau}
        </button>
      </form>
    ),
  };

  return (
    <div className="man-hinh ho-so man-hinh--ho-so-ca-nhan">
      <h1 className="chi-cho-doc-man-hinh">{t.tieuDe}</h1>

      <aside className="ho-so__the ho-so__the--trai" aria-label={vi.hoSoCaNhan.thongTinTaiKhoan}>
        <div className="ho-so__anh" role="img" aria-label={t.anhDaiDien}>
          <BieuTuong ten="nguoi-dung" co={56} />
        </div>
        <p className="ho-so__ten">{hoSo?.tenDangNhap ?? '—'}</p>
        <dl className="ho-so__lien-he">
          <dt>{t.email}</dt>
          <dd>{hoSo?.email || '—'}</dd>
          <dt>{t.dienThoai}</dt>
          <dd>{hoSo?.dienThoai || '—'}</dd>
        </dl>
        <div className="ho-so__lien-ket">
          <Link to="/quan-tri/phien" className="nut">
            {vi.thietBiTinCayPhien.tieuDe}
          </Link>
        </div>
      </aside>

      <section className="ho-so__the">
        <div className="tab" role="tablist">
          {CAC_TAB.map(([k, nhan]) => (
            <button
              key={k}
              type="button"
              role="tab"
              id={`tab-ho-so-${k}`}
              aria-selected={tab === k}
              aria-controls="tab-ho-so-noi-dung"
              className={`tab__nut${tab === k ? ' tab__nut--dang-chon' : ''}`}
              onClick={() => {
                datTab(k);
                datDangSua(false);
              }}
            >
              {nhan}
            </button>
          ))}
        </div>

        {thongBao ? <p role="status">{thongBao}</p> : null}
        {loi ? <p role="alert">{loi}</p> : null}

        <div id="tab-ho-so-noi-dung" role="tabpanel" aria-labelledby={`tab-ho-so-${tab}`}>
          {noiDungTab[tab]}
        </div>
      </section>
    </div>
  );
}
